import React, { useState, useMemo, useEffect, useRef, memo } from 'react';
import BudBurstAnimation from './BudBurstAnimation';
import TokenDropAnimation from './TokenDropAnimation';
import '../styles/spinwheel.css';

const FALLBACK_SEGMENTS = [
  { label: '+10 BP', color: '#2d6a30', rarity: 'common' },
  { label: '+20 BP', color: '#38a169', rarity: 'common' },
  { label: '+30 BP', color: '#2f855a', rarity: 'common' },
  { label: '+40 BP', color: '#276749', rarity: 'common' },
  { label: '+50 BP', color: '#3182ce', rarity: 'uncommon' },
  { label: '+75 BP', color: '#2b6cb0', rarity: 'uncommon' },
  { label: '+100 BP', color: '#2c5282', rarity: 'uncommon' },
  { label: '+150 BP', color: '#805ad5', rarity: 'rare' },
  { label: '+200 BP', color: '#6b46c1', rarity: 'rare' },
  { label: '+500 BP', color: '#9f7aea', rarity: 'rare' },
  { label: '+800 BP', color: '#b794f4', rarity: 'rare' },
  { label: '+1500 BP', color: '#d53f8c', rarity: 'legendary' },
  { label: '1g Nug', color: '#d69e2e', rarity: 'rare' },
  { label: '3.5g Eighth', color: '#dd6b20', rarity: 'legendary' },
  { label: '14g Jackpot', color: '#e53e3e', rarity: 'legendary' },
  { label: 'Lost Out', color: '#2d2d2d', rarity: 'common' },
];

const RARITY_COLORS = {
  common: ['#2d6a30', '#38a169', '#2f855a', '#276749'],
  uncommon: ['#3182ce', '#2b6cb0', '#2c5282'],
  rare: ['#805ad5', '#6b46c1', '#9f7aea', '#b794f4', '#d69e2e'],
  legendary: ['#d53f8c', '#dd6b20', '#e53e3e'],
};

function getColorForRarity(rarity, index) {
  const colors = RARITY_COLORS[rarity] || RARITY_COLORS.common;
  return colors[index % colors.length];
}

// Spread segments so no two adjacent share the same rarity (round-robin interleave).
// Lost Out segments are always placed at evenly-spaced positions so they never appear close together.
function spreadByRarity(segs) {
  if (segs.length <= 2) return segs;

  // Separate Lost Out slots — they get fixed evenly-spaced positions
  const lostOuts = segs.filter(s => s.prize_type === 'none');
  const rest = segs.filter(s => s.prize_type !== 'none');

  // Round-robin interleave remaining prize segments by rarity
  const groups = {};
  rest.forEach(s => { (groups[s.rarity] = groups[s.rarity] || []).push(s); });
  Object.values(groups).forEach(g => {
    for (let i = g.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [g[i], g[j]] = [g[j], g[i]];
    }
  });
  const sorted = Object.values(groups).sort((a, b) => b.length - a.length);
  const prizes = [];
  const maxLen = Math.max(...sorted.map(g => g.length));
  for (let i = 0; i < maxLen; i++) {
    for (const group of sorted) {
      if (i < group.length) prizes.push(group[i]);
    }
  }

  // Place Lost Outs at evenly-spaced indices across the full segment list
  const total = segs.length;
  const result = new Array(total).fill(null);
  const spacing = lostOuts.length > 0 ? Math.floor(total / lostOuts.length) : total;
  lostOuts.forEach((lo, i) => { result[i * spacing] = lo; });

  // Fill remaining slots with prize segments in order
  let pi = 0;
  for (let i = 0; i < total; i++) {
    if (result[i] === null) result[i] = prizes[pi++];
  }

  return result;
}

function buildDisplayLabel(seg) {
  if (seg.prize_type === 'points') return `+${seg.prize_value} BP`;
  if (seg.prize_type === 'coupon') return seg.coupon_grams ? `${seg.coupon_grams} Nug` : seg.label;
  if (seg.prize_type === 'none') return 'Lost Out';
  return seg.label;
}

const SIZE = 320;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 2;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx},${cy} L ${start.x},${start.y} A ${r},${r} 0 ${largeArc},0 ${end.x},${end.y} Z`;
}

// Memoized so smoke puff state changes (every 150ms) never trigger SVG re-renders
const WheelFace = memo(function WheelFace({ segments, rotation }) {
  const segmentAngle = 360 / segments.length;
  return (
    <svg
      className="wheel"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      {segments.map((seg, i) => {
        const startAngle = i * segmentAngle;
        const endAngle = (i + 1) * segmentAngle;
        const midAngle = startAngle + segmentAngle / 2;
        const labelPos = polarToCartesian(CENTER, CENTER, RADIUS * 0.65, midAngle);
        const textRotation = midAngle - 90;
        return (
          <g key={i}>
            <path
              d={describeArc(CENTER, CENTER, RADIUS, startAngle, endAngle)}
              fill={seg.color}
              stroke="#1a1a2e"
              strokeWidth="1.5"
            />
            <text
              x={labelPos.x}
              y={labelPos.y}
              fill="white"
              fontSize="7.5"
              fontWeight="700"
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${textRotation}, ${labelPos.x}, ${labelPos.y})`}
              style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)', textTransform: 'uppercase', letterSpacing: '0.3px' }}
            >
              {seg.label}
            </text>
          </g>
        );
      })}
      <circle cx={CENTER} cy={CENTER} r="24" fill="#0a1a0f" stroke="#1e3a28" strokeWidth="2" />
      <text x={CENTER} y={CENTER} fill="#38a169" fontSize="14" fontWeight="800" textAnchor="middle" dominantBaseline="middle">&#127807;</text>
    </svg>
  );
});

export default function SpinWheel({ onSpin, spinTokens, segmentsFromApi }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showBudBurst, setShowBudBurst] = useState(null);
  const [showTokenDrop, setShowTokenDrop] = useState(null);
  const [smokePuffs, setSmokePuffs] = useState([]);
  const smokeInterval = useRef(null);

  // Build display segments from API data or fallback, spread by rarity, track index mapping
  const { segments, indexMap } = useMemo(() => {
    const raw = (segmentsFromApi && segmentsFromApi.length > 0)
      ? segmentsFromApi.map((seg, i) => ({
          label: buildDisplayLabel(seg),
          color: seg.prize_type === 'none' ? '#6b7280' : getColorForRarity(seg.rarity, i),
          rarity: seg.rarity,
          _origIdx: i,
        }))
      : FALLBACK_SEGMENTS.map((seg, i) => ({ ...seg, _origIdx: i }));

    const spread = spreadByRarity(raw);
    const map = {};
    spread.forEach((seg, displayIdx) => { map[seg._origIdx] = displayIdx; });
    return { segments: spread, indexMap: map };
  // eslint-disable-next-line
  }, [segmentsFromApi?.length]);

  useEffect(() => {
    if (spinning) {
      const spawn = () => {
        const angle = Math.random() * 360;
        const dist = 105 + Math.random() * 55;
        const travel = 90 + Math.random() * 130;
        const dx = Math.cos(angle * Math.PI / 180) * travel;
        const dy = Math.sin(angle * Math.PI / 180) * travel;
        const puff = {
          id: Date.now() + Math.random(),
          x: 160 + Math.cos(angle * Math.PI / 180) * dist,
          y: 160 + Math.sin(angle * Math.PI / 180) * dist,
          dx, dy,
          size: 55 + Math.random() * 65,
        };
        setSmokePuffs(prev => [...prev.slice(-10), puff]);
      };
      spawn();
      smokeInterval.current = setInterval(spawn, 220);
      return () => clearInterval(smokeInterval.current);
    } else {
      clearInterval(smokeInterval.current);
      const timer = setTimeout(() => setSmokePuffs([]), 2500);
      return () => clearTimeout(timer);
    }
  }, [spinning]);

  const handleSpin = async () => {
    if (spinning || spinTokens <= 0) return;
    setSpinning(true);
    setResult(null);
    setShowBudBurst(null);
    setShowTokenDrop(null);

    try {
      const data = await onSpin();
      const displayIdx = indexMap[data.segmentIndex] ?? data.segmentIndex;
      const segmentAngle = 360 / segments.length;
      const targetAngle = 360 - (displayIdx * segmentAngle) - (segmentAngle / 2);
      const currentAngle = rotation % 360;
      let delta = targetAngle - currentAngle;
      if (delta <= 0) delta += 360;
      const newRotation = rotation + (5 * 360) + delta;
      setRotation(newRotation);

      setTimeout(() => {
        setResult(data.outcome);
        setSpinning(false);

        if (data.outcome.prize_type === 'points') {
          setShowBudBurst(parseInt(data.outcome.prize_value, 10));
        } else if (data.outcome.prize_type === 'coupon') {
          setShowTokenDrop(data.outcome.coupon_grams + ' Nug Drop!');
        }
      }, 4200);
    } catch (err) {
      setResult({ error: err.message });
      setSpinning(false);
    }
  };

  const getRarityClass = (r) => {
    if (!r || r.error) return 'common';
    return r.rarity || 'common';
  };

  const getResultText = (r) => {
    if (!r) return '';
    if (r.prize_type === 'none') return 'Better luck next time!';
    if (r.prize_type === 'points') return `+${r.prize_value} Bud Points`;
    if (r.prize_type === 'coupon') return `${r.coupon_grams} Nug - Free Pickup!`;
    return '';
  };

  return (
    <div className="spin-wheel-container">
      <div className="spin-tokens-display">
        Toke Tokens: <strong>{spinTokens}</strong>
      </div>
      <div className={`wheel-wrapper${spinning ? ' spinning' : ''}`}>
        <div className="wheel-pointer" />
        <div className="wheel-glow-ring" />
        {smokePuffs.length > 0 && (
          <div className="smoke-container">
            {smokePuffs.map(p => (
              <div
                key={p.id}
                className="smoke-puff"
                style={{
                  left: p.x, top: p.y,
                  width: p.size, height: p.size,
                  '--dx': `${p.dx}px`, '--dy': `${p.dy}px`,
                }}
              />
            ))}
            {spinning && <div className="smoke-swirl" />}
          </div>
        )}
        <WheelFace segments={segments} rotation={rotation} />
      </div>
      <button
        className="spin-button"
        onClick={handleSpin}
        disabled={spinning || spinTokens <= 0}
      >
        {spinning ? 'Rolling...' : spinTokens > 0 ? 'LIGHT IT UP!' : 'No Tokens'}
      </button>
      {result && !result.error && (
        <div className={`spin-result rarity-${getRarityClass(result)}`} style={{ position: 'relative', overflow: 'visible' }}>
          {result.prize_type !== 'none' && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="smoke-burst" style={{
                  left: `${20 + Math.random() * 60}%`,
                  top: `${-10 + Math.random() * 40}%`,
                  animationDelay: `${i * 0.15}s`,
                }} />
              ))}
            </div>
          )}
          <div className="spin-result-label">{result.label}!</div>
          <div className="spin-result-detail">{getResultText(result)}</div>
          {result.coupon_code && (
            <div className="spin-coupon-code">{result.coupon_code}</div>
          )}
        </div>
      )}
      {result?.error && (
        <div className="spin-result rarity-common">{result.error}</div>
      )}

      {showBudBurst && (
        <BudBurstAnimation points={showBudBurst} onComplete={() => setShowBudBurst(null)} />
      )}
      {showTokenDrop && (
        <TokenDropAnimation label={showTokenDrop} onComplete={() => setShowTokenDrop(null)} />
      )}
    </div>
  );
}
